import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Tab,
  Typography,
  useMediaQuery
} from '@material-ui/core'
import {
  AutorenewOutlined as AutorenewOutlinedIcon,
  CancelOutlined as CancelOutlinedIcon,
  Close as CloseIcon,
  DoneOutline as DoneOutlineIcon
} from '@material-ui/icons'
import { TabContext, TabList, TabPanel } from '@material-ui/lab'
import clsx from 'clsx'
import lodash from 'lodash'
import { EventsProxy } from 'services'
import { EllipseStatus } from 'shared'
import {
  loadEventInscriptionsAllForCurrentUser,
  loadEventInscriptionsForCurrentUser,
  resetEventInscriptionsForCurrentUser
} from 'state/modules/events'
import { EVENT_INSCRIPTION_STATES, STRING_EMPTY } from 'utils/constants'
import { showSnackbarError } from 'utils/snackbar'

import { InscriptionList } from '../InscriptionList'

import { useStyles } from './StateInscriptionDialog.style'

const INSCRIPTIONS_SAME_CATEGORY_TAB_INDEX = '1'
const ALL_INSCRIPTIONS_TAB_INDEX = '2'
const PAGE_SIZE_MAX = 20

const StateInscriptionDialog = ({ open, onClose, title, inscriptionsCount }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { user } = useSelector((state) => state.auth)
  const {
    event,
    eventInscriptionsForCurrentUserPending,
    eventInscriptionsForCurrentUser,
    eventInscriptionAllForUserPageLastPage,
    eventInscriptionAllForUserPageNumber,
    eventInscriptionAllForUser,
    eventInscriptionAllForUserPending
  } = useSelector((state) => state.events)

  const containerRef = useRef(null)
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('xs'))
  const activeLoading =
    (!eventInscriptionsForCurrentUser &&
      !eventInscriptionsForCurrentUserPending &&
      !eventInscriptionAllForUser?.length) ||
    eventInscriptionsForCurrentUserPending

  const [tabSelected, setTabSelected] = useState(INSCRIPTIONS_SAME_CATEGORY_TAB_INDEX)
  const [inscriptionFormItemSelected, setInscriptionFormItemSelected] = useState(null)
  const [inscriptionFormItemState, setInscriptionFormItemState] = useState(null)
  const [inscriptionFormItemsForCurrentUser, setInscriptionFormItemsForCurrentUser] = useState([])
  const [editActive, setEditActive] = useState(false)

  useEffect(() => {
    if (user && event) {
      loadInscriptionFormItemsForCurrentUser()
    }
  }, [dispatch, event, user])

  useEffect(() => {
    if (inscriptionFormItemSelected) {
      const item = inscriptionFormItemSelected

      dispatch(
        loadEventInscriptionsForCurrentUser(
          event.id,
          1,
          PAGE_SIZE_MAX,
          item?.eventDistanceId,
          item?.eventCategoryId
        )
      )
    }

    return () => {
      dispatch(resetEventInscriptionsForCurrentUser())
    }
  }, [dispatch, inscriptionFormItemSelected])

  useEffect(() => {
    if (!eventInscriptionsForCurrentUser || !eventInscriptionsForCurrentUser.inscription) {
      setTabSelected(ALL_INSCRIPTIONS_TAB_INDEX)
    } else {
      setTabSelected(INSCRIPTIONS_SAME_CATEGORY_TAB_INDEX)
    }
  }, [eventInscriptionsForCurrentUser])

  const handleTabsChange = (_, newValue) => setTabSelected(newValue)

  const loadInscriptionFormItemsForCurrentUser = async () => {
    try {
      const proxy = new EventsProxy()
      const data = await proxy.getInscriptionFormItemsForEventAndCurrentUser(event.id)

      if (data.length) {
        setInscriptionFormItemsForCurrentUser(data)
        setInscriptionFormItemSelected(data[0])
        setInscriptionFormItemState(data[0])
      } else {
        setInscriptionFormItemSelected({})
      }

      return data
    } catch (error) {
      showSnackbarError(error)
    }
  }

  const renderLoading = () => (
    <div className={classes.loadingContainer}>
      <CircularProgress color='primary' size={46} />
    </div>
  )

  const handleMoreShow = async () => {
    await dispatch(
      loadEventInscriptionsAllForCurrentUser(
        event.id,
        eventInscriptionAllForUserPageNumber,
        PAGE_SIZE_MAX,
        inscriptionFormItemSelected.distanceId,
        inscriptionFormItemSelected.categoryId
      )
    )
  }

  const handleActiveEditOrSave = () => {
    if (!editActive) {
      setEditActive(true)
      return
    }

    setInscriptionFormItemSelected(inscriptionFormItemState)
    setEditActive(false)
  }

  const hasMultipleInscriptionFormItems = inscriptionFormItemsForCurrentUser.length > 1
  const distances = lodash.uniqBy(inscriptionFormItemsForCurrentUser, 'eventDistanceId')
  const categories = lodash.uniqBy(
    inscriptionFormItemsForCurrentUser.filter(
      (x) => x.eventDistanceId === inscriptionFormItemState?.eventDistanceId
    ),
    'eventCategoryId'
  )

  const handleClose = () => {
    onClose()
    setEditActive(false)
  }

  const renderContentDialog = () => {
    const { inscription, inscriptionsSameCategory } = eventInscriptionsForCurrentUser

    const stateInscription = (state) => {
      switch (state) {
        case EVENT_INSCRIPTION_STATES.PENDING:
          return (
            <>
              <EllipseStatus status='yellow' />
              &nbsp;PREINSCRIPTO
            </>
          )
        case EVENT_INSCRIPTION_STATES.ACCEPTED:
          return (
            <>
              <EllipseStatus status='green' />
              &nbsp;INSCRIPTO
            </>
          )
        case EVENT_INSCRIPTION_STATES.CANCELLED:
          return (
            <>
              <EllipseStatus status='red' />
              &nbsp;ANULADO
            </>
          )

        default:
          return <></>
      }
    }

    return (
      <>
        {inscription && (
          <div className={classes.bodyContainer}>
            <div className={classes.userStateContainer}>
              <Typography color='primary' variant='h6'>
                MI ESTADO
              </Typography>
              <Typography color='primary' variant='h6' className={classes.userState}>
                {stateInscription(inscription.state)}
              </Typography>
            </div>
            <div className={classes.userStateContainer}>
              <div className={classes.optionContainer}>
                <div className={classes.infoContainer}>
                  {!editActive ? (
                    <Typography className={classes.info}>
                      <strong>Distancia:</strong>&nbsp;
                      {inscription.distance.name}
                    </Typography>
                  ) : (
                    <div className={classes.editBlock}>
                      <Typography className={classes.info}>
                        <strong>Distancia:</strong>&nbsp;
                      </Typography>

                      <Select
                        value={inscriptionFormItemState?.eventDistanceId}
                        color='primary'
                        variant='outlined'
                        className={classes.select}
                        onChange={(e) =>
                          setInscriptionFormItemState({
                            ...inscriptionFormItemState,
                            eventDistanceId: e.target.value,
                            eventCategoryId:
                              inscriptionFormItemsForCurrentUser.find(
                                (x) => x.eventDistanceId === e.target.value
                              )?.eventCategoryId ?? STRING_EMPTY
                          })
                        }>
                        {distances.map((item) => (
                          <MenuItem key={item.eventDistanceId} value={item.eventDistanceId}>
                            {item.eventDistanceName}
                          </MenuItem>
                        ))}
                      </Select>
                    </div>
                  )}

                  {!editActive ? (
                    <Typography className={classes.info}>
                      <strong>Categoría:</strong>&nbsp;
                      {inscription.category.name}
                    </Typography>
                  ) : (
                    <div className={classes.editBlock}>
                      <Typography className={classes.info}>
                        <strong>Categoría:</strong>&nbsp;
                      </Typography>

                      <Select
                        value={inscriptionFormItemState?.eventCategoryId}
                        color='primary'
                        variant='outlined'
                        className={classes.select}
                        onChange={(e) =>
                          setInscriptionFormItemState({
                            ...inscriptionFormItemState,
                            eventCategoryId: e.target.value
                          })
                        }>
                        {categories.map((item) => (
                          <MenuItem key={item.eventCategoryId} value={item.eventCategoryId}>
                            {item.eventCategoryName}
                          </MenuItem>
                        ))}
                      </Select>
                    </div>
                  )}
                </div>

                <div className={classes.buttonContainer}>
                  {hasMultipleInscriptionFormItems && (
                    <IconButton
                      color='primary'
                      size='small'
                      onClick={handleActiveEditOrSave}
                      className={classes.editButton}>
                      {!editActive ? <AutorenewOutlinedIcon /> : <DoneOutlineIcon />}
                    </IconButton>
                  )}
                  {editActive && (
                    <IconButton
                      color='primary'
                      size='small'
                      onClick={() => setEditActive(false)}
                      className={classes.editButton}>
                      <CancelOutlinedIcon />
                    </IconButton>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <TabContext id='scroll-dialog-description' value={tabSelected}>
          <TabList
            className={classes.tabs}
            centered
            onChange={handleTabsChange}
            indicatorColor='secondary'
            variant='fullWidth'
            textColor='secondary'>
            <Tab
              className={clsx(classes.tab, !inscriptionsSameCategory && classes.tabHidden)}
              label={`Mi categoría (${inscriptionsSameCategory?.length})`}
              color='secondary'
              value={INSCRIPTIONS_SAME_CATEGORY_TAB_INDEX}
              selected
            />

            <Tab
              className={classes.tab}
              label={`Todos los inscriptos (${inscriptionsCount})`}
              color='secondary'
              value={ALL_INSCRIPTIONS_TAB_INDEX}
              selected
            />
          </TabList>
          <div className={clsx(classes.subtitle, inscription && 'enrolled')}>
            <Typography color='primary' className={classes.subtitleText}>
              Nombre
            </Typography>
            {tabSelected === ALL_INSCRIPTIONS_TAB_INDEX && (
              <Typography color='primary' className={classes.subtitleText}>
                Distancia y categoría
              </Typography>
            )}
          </div>
          <DialogContent className={classes.dialogContent} ref={containerRef}>
            <TabPanel
              value={INSCRIPTIONS_SAME_CATEGORY_TAB_INDEX}
              color='primary'
              style={{ padding: 0 }}>
              {inscriptionsSameCategory && (
                <InscriptionList
                  title={`${inscription.category.name} - ${inscription.distance.name}`}
                  category
                  listInscription={
                    inscriptionsSameCategory.map((e) => ({
                      fullName: `${e.lastName} ${e.firstName}`,
                      id: e.inscriptionUserId,
                      loggedUserMetadata: e.loggedUserMetadata
                    })) || []
                  }
                />
              )}
            </TabPanel>

            <TabPanel
              value={ALL_INSCRIPTIONS_TAB_INDEX}
              color='primary'
              className={classes.allInscriptionsContainer}>
              {!!eventInscriptionAllForUser.length && (
                <>
                  <InscriptionList
                    hiddenHeader={!inscriptionsSameCategory}
                    listInscription={eventInscriptionAllForUser.map((e) => ({
                      fullName: `${e.lastName} ${e.firstName}`,
                      distanceAndCategory: `${e.distance.name}-${e.category.name}`,
                      id: e.inscriptionUserId,
                      loggedUserMetadata: e.loggedUserMetadata
                    }))}
                  />
                  {!eventInscriptionAllForUserPageLastPage && !eventInscriptionAllForUserPending && (
                    <Button
                      color='primary'
                      variant='contained'
                      className={classes.button}
                      onClick={handleMoreShow}>
                      Ver más
                    </Button>
                  )}
                  {eventInscriptionAllForUserPending && (
                    <div className={classes.loadingContainer}>
                      <CircularProgress size={30} />
                    </div>
                  )}
                </>
              )}
            </TabPanel>
          </DialogContent>
        </TabContext>
      </>
    )
  }
  return (
    <Dialog
      BackdropProps={{
        classes: {
          root: classes.backDrop
        }
      }}
      fullWidth
      fullScreen={isMobile}
      open={open}
      maxWidth='md'
      aria-labelledby='scroll-dialog-title'
      aria-describedby='scroll-dialog-description'
      onClose={handleClose}
      scroll='paper'
      className={classes.container}>
      <DialogTitle className={classes.dialogTitle}>
        <div className={classes.headerContainer}>
          <Typography
            color='primary'
            align='center'
            variant='h6'
            className={classes.title}
            title={title}>
            {title}
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon color='secondary' />
          </IconButton>
        </div>
      </DialogTitle>
      {activeLoading ? renderLoading() : renderContentDialog()}
    </Dialog>
  )
}

export default StateInscriptionDialog
